import { Card, genericMemo, Text } from "@overmap-ai/blocks"
import { pluralize } from "@overmap-ai/common"
import { selectUserById } from "@overmap-ai/core"
import { cx } from "class-variance-authority"
import { useCallback } from "react"

import { interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector } from "../../store"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { useDeserializeFieldSchema } from "../Form/hooks"
import { TimelineIconRail } from "../TimelineIconRail/TimelineIconRail"
import type { FieldsRevisionModel } from "./typings"

interface FieldsRevisionsTimelineItemProps<TModel extends FieldsRevisionModel> {
	fieldsRevision: TModel
	onSelect: (fieldsRevision: TModel) => void
}

export const FieldsRevisionsTimelineItem = genericMemo(function <TModel extends FieldsRevisionModel>(
	props: FieldsRevisionsTimelineItemProps<TModel>,
) {
	const { fieldsRevision, onSelect } = props
	const createdBy = useAppSelector(selectUserById(fieldsRevision.created_by ?? -1))
	const fieldSchema = useDeserializeFieldSchema(fieldsRevision.fields)

	const fieldSectionCount = fieldSchema.getFieldSections().length
	const fieldsCount = fieldSchema.getFields().length

	const handleClick = useCallback(() => {
		onSelect(fieldsRevision)
	}, [fieldsRevision, onSelect])

	return (
		<div className="flex h-max w-full gap-4">
			<TimelineIconRail icon={modelIconMapping.edit} size="sm" />
			<button type="button" className="min-w-0 grow text-left" onClick={handleClick}>
				<Card className={cx("flex cursor-pointer flex-col gap-1", interactiveCardCva())} size="sm">
					<Text size="sm" accentColor="base">
						<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
						&nbsp;created a fields revision&nbsp;&#8226;&nbsp;
						{formatCompactDate(fieldsRevision.submitted_at)}
					</Text>

					<Text size="sm" accentColor="base">
						<Text highContrast>
							{fieldsRevision.revision === undefined
								? "Pending revision"
								: `Revision ${fieldsRevision.revision}`}
						</Text>
						&nbsp;&#8226;&nbsp;
						{fieldSectionCount} {pluralize("section", fieldSectionCount)}
						,&nbsp;
						{fieldsCount} {pluralize("field", fieldsCount)}
					</Text>
				</Card>
			</button>
		</div>
	)
})
